import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import PageEditor from '../components/PageEditor';
import SimplifiedPageEditor from '../components/SimplifiedPageEditor';
import HeroEditor from '../components/HeroEditor';
import Pagination from '../components/Pagination';
import { compressImage, needsCompression } from '../utils/imageCompression';
import '../styles/Admin.css';

// Get API URL from environment
const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function Admin() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const token = useAuthStore((state) => state.token);

  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [orders, setOrders] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [wishlists, setWishlists] = useState([]);
  const [categories, setCategories] = useState([]);
  const [colors, setColors] = useState([]);
  const [customPages, setCustomPages] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newColorForm, setNewColorForm] = useState({ colorName: '', colorHex: '#000000' });
  const [editingColor, setEditingColor] = useState(null);
  const [editColorForm, setEditColorForm] = useState({ colorName: '', colorHex: '#000000' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showPageEditor, setShowPageEditor] = useState(false);
  const [showSimplifiedEditor, setShowSimplifiedEditor] = useState(false);
  const [showEditorChoice, setShowEditorChoice] = useState(false);
  const [showHeroEditor, setShowHeroEditor] = useState(false);
  const [newPageId, setNewPageId] = useState('');
  const [selectedPageForEdit, setSelectedPageForEdit] = useState(null);
  const [newPageForm, setNewPageForm] = useState({ pageId: '', title: '', content: '' });
  const [galleryImages, setGalleryImages] = useState([]);
  const [footerConfig, setFooterConfig] = useState(() => {
    const saved = localStorage.getItem('footerConfig');
    return saved ? JSON.parse(saved) : {
      column1: {
        title: 'Customer Service',
        links: [
          { label: 'Contact us', pageId: 'contact' },
          { label: 'Size guide', pageId: 'size-guide' },
          { label: 'Delivery', pageId: 'delivery' },
          { label: 'Return & exchanges', pageId: 'returns-and-exchange' },
          { label: 'Care of products', pageId: 'care-of-products' }
        ]
      },
      column2: {
        title: 'Company Information',
        links: [
          { label: 'About', pageId: 'about' },
          { label: 'Terms & Conditions', pageId: 'terms' },
          { label: 'Privacy Policy', pageId: 'privacy' }
        ]
      }
    };
  });

  // Staging footer config for changes before saving
  const [footerConfigStaging, setFooterConfigStaging] = useState(footerConfig);

  const [newCategory, setNewCategory] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [statusUpdateModal, setStatusUpdateModal] = useState(null);
  const [statusUpdateForm, setStatusUpdateForm] = useState({ status: '', customerMessage: '', adminNotes: '' });
  const [productSearch, setProductSearch] = useState('');
  const [productStatusFilter, setProductStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [featuredProductSearch, setFeaturedProductSearch] = useState('');

  // Pagination state
  const [productsPagination, setProductsPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [usersPagination, setUsersPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [ordersPagination, setOrdersPagination] = useState({ page: 1, limit: 30, total: 0, totalPages: 0 });

  // SIDOBE WhatsApp Settings State
  const [whatsappSettings, setWhatsappSettings] = useState({
    whatsappPhoneId: '',
    whatsappAccessToken: '',
    notificationsEnabled: true,
    notifyOnOrders: true,
    notifyOnContact: true
  });
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappMessage, setWhatsappMessage] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    price: '',
    description: '',
    sku: '',
    discountPrice: 0,
    stockBySize: {},
  });


  const [discountForm, setDiscountForm] = useState({
    code: '',
    percentage: '',
    expiryDate: '',
  });

  const [selectedProductImages, setSelectedProductImages] = useState({});

  // Check if user is admin
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
    }
  }, [user, navigate]);

  // Fetch WhatsApp Business API settings
  const fetchWhatsappSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/whatsapp-settings`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setWhatsappSettings({
          whatsappPhoneId: data.whatsapp_phone_id || '',
          whatsappAccessToken: data.whatsapp_access_token || '',
          notificationsEnabled: data.notifications_enabled !== 0,
          notifyOnOrders: data.notify_on_orders !== 0,
          notifyOnContact: data.notify_on_contact !== 0
        });
      }
    } catch (error) {
      console.error('Error fetching WhatsApp Business API settings:', error);
    }
  }, [token]);

  // Update WhatsApp Business API settings
  const handleUpdateWhatsappSettings = async (e) => {
    e.preventDefault();
    setWhatsappLoading(true);
    setWhatsappMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/whatsapp-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          whatsapp_phone_id: whatsappSettings.whatsappPhoneId,
          whatsapp_access_token: whatsappSettings.whatsappAccessToken,
          notifications_enabled: whatsappSettings.notificationsEnabled ? 1 : 0,
          notify_on_orders: whatsappSettings.notifyOnOrders ? 1 : 0,
          notify_on_contact: whatsappSettings.notifyOnContact ? 1 : 0
        })
      });

      if (response.ok) {
        setWhatsappMessage('✓ WhatsApp Business API settings saved successfully');
        setTimeout(() => setWhatsappMessage(''), 3000);
      } else {
        const data = await response.json();
        setWhatsappMessage(`✗ Error: ${data.error || 'Failed to save settings'}`);
      }
    } catch (error) {
      console.error('Error updating WhatsApp Business API settings:', error);
      setWhatsappMessage('✗ Error saving settings');
    } finally {
      setWhatsappLoading(false);
    }
  };

  // Test WhatsApp Business API connection
  const handleTestWhatsapp = async () => {
    if (!whatsappSettings.whatsappPhoneId || !whatsappSettings.whatsappAccessToken) {
      setWhatsappMessage('✗ Please enter both WhatsApp Phone Number ID and Access Token');
      return;
    }

    setWhatsappLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/whatsapp-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          whatsapp_phone_id: whatsappSettings.whatsappPhoneId,
          whatsapp_access_token: whatsappSettings.whatsappAccessToken
        })
      });

      if (response.ok) {
        setWhatsappMessage('✓ WhatsApp Business API connection successful! Test message sent to your admin phone.');
        setTimeout(() => setWhatsappMessage(''), 5000);
      } else {
        const data = await response.json();
        setWhatsappMessage(`✗ Connection failed: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error testing WhatsApp Business API connection:', error);
      setWhatsappMessage('✗ Error testing connection');
    } finally {
      setWhatsappLoading(false);
    }
  };

  // Fetch footer configuration from database
  const fetchFooterConfig = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/footer-config`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setFooterConfig({
          column1: {
            title: data.column1?.title || 'Customer Service',
            links: Array.isArray(data.column1?.links) ? data.column1.links : []
          },
          column2: {
            title: data.column2?.title || 'Company Information',
            links: Array.isArray(data.column2?.links) ? data.column2.links : []
          }
        });
      }
    } catch (error) {
      console.error('Error fetching footer config:', error);
    }
  }, [token]);

  // Load footer config from database when component mounts
  useEffect(() => {
    if (token) {
      fetchFooterConfig();
    }
  }, [token, fetchFooterConfig]);

  // Sync staging with footerConfig when it changes
  useEffect(() => {
    setFooterConfigStaging(footerConfig);
  }, [footerConfig]);

  // Auto-clear messages after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchUsers = useCallback(async (page = usersPagination.page) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/admin?page=${page}&limit=${usersPagination.limit}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();

      // Handle both old (array) and new (paginated object) response formats
      if (data.data && data.pagination) {
        setUsers(Array.isArray(data.data) ? data.data : []);
        setUsersPagination(data.pagination);
      } else {
        setUsers(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [token, usersPagination.page, usersPagination.limit]);

  const getEnabledSizes = (product) => {
    if (!product.stockByColor || typeof product.stockByColor !== 'object') {
      return [];
    }

    const sizes = new Set();
    Object.values(product.stockByColor).forEach(colorStock => {
      if (colorStock && typeof colorStock === 'object') {
        Object.keys(colorStock).forEach(size => {
          sizes.add(size);
        });
      }
    });
    return Array.from(sizes).sort((a, b) => parseFloat(a) - parseFloat(b));
  };

  const fetchProducts = useCallback(async (page = productsPagination.page, fetchAll = false) => {
    setLoading(true);
    console.log('=== fetchProducts called ===');
    console.log('Parameters:', { page, fetchAll, currentPaginationLimit: productsPagination.limit, productStatusFilter });
    try {
      // If fetchAll is true OR a filter is active, fetch all products without pagination
      const shouldFetchAll = fetchAll || productStatusFilter !== 'all';
      const url = shouldFetchAll
        ? `${API_BASE_URL}/api/products/admin?page=1&limit=9999`
        : `${API_BASE_URL}/api/products/admin?page=${page}&limit=${productsPagination.limit}`;

      console.log('Fetching from URL:', url);

      // Try admin endpoint first (includes stockByColor), fallback to public endpoint
      let response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      let data;
      if (response.ok) {
        data = await response.json();
        console.log(`Fetched products from /api/products/admin${fetchAll ? ' (all products)' : ''}`);
        console.log('Response data:', { hasData: !!data.data, hasPagination: !!data.pagination, totalProducts: data.data?.length || data.length });
      } else {
        // Fallback to public endpoint with images
        response = await fetch(`${API_BASE_URL}/api/products/with-images`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        data = await response.json();
        console.log('Fallback: Fetched products from /api/products/with-images');
      }

      // Handle both old (array) and new (paginated object) response formats
      let productsData;
      if (data.data && data.pagination) {
        productsData = Array.isArray(data.data) ? data.data : [];
        // Only update pagination state if not fetching all
        if (!fetchAll) {
          setProductsPagination(data.pagination);
        }
      } else {
        productsData = Array.isArray(data) ? data : [];
      }

      // Log first product to verify stockByColor structure
      if (productsData.length > 0) {
        console.log('First product stockByColor:', productsData[0].stockByColor);
        console.log('First product enabled sizes:', getEnabledSizes(productsData[0]));
      }

      console.log('Setting products state with', productsData.length, 'products');
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [token, productsPagination.page, productsPagination.limit, productStatusFilter]);

  const fetchOrders = useCallback(async (page = ordersPagination.page) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/admin?page=${page}&limit=${ordersPagination.limit}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();

      // Handle both old (array) and new (paginated object) response formats
      if (data.data && data.pagination) {
        setOrders(Array.isArray(data.data) ? data.data : []);
        setOrdersPagination(data.pagination);
      } else {
        setOrders(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [token, ordersPagination.page, ordersPagination.limit]);

  const fetchDiscounts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/discounts/admin`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      setDiscounts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching discounts:', error);
      setDiscounts([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchWishlists = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/wishlists/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      setWishlists(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching wishlists:', error);
      setWishlists([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchCustomPages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/pages/list`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      // Show ALL pages (no filtering)
      const pages = Array.isArray(data) ? data : [];
      setCustomPages(pages);
    } catch (error) {
      console.error('Error fetching pages:', error);
      setCustomPages([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchFeaturedProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/featured-products`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      setFeaturedProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching featured products:', error);
      setFeaturedProducts([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch categories from API
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/categories`);
      if (response.ok) {
        const data = await response.json();
        const categoryNames = data.map(cat => cat.name);
        setCategories(categoryNames);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories(['Mens', 'Womens', 'Kids', 'Sports']);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const fetchColors = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/colors`);
      if (response.ok) {
        const data = await response.json();
        setColors(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching colors:', error);
      setColors([]);
    }
  }, []);

  const fetchGalleryImages = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gallery`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGalleryImages(Array.isArray(data) ? data : []);
      } else {
        setGalleryImages([]);
      }
    } catch (error) {
      console.error('Error fetching gallery images:', error);
      setGalleryImages([]);
    }
  }, [token]);

  const handleUploadGalleryImage = async (file) => {
    if (!file) return;

    try {
      setLoading(true);
      setMessage('Uploading...');

      const formData = new FormData();
      formData.append('image', file); // Backend expects 'image' not 'file'

      const response = await fetch(`${API_BASE_URL}/api/gallery/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        setMessage('Image uploaded successfully!');
        fetchGalleryImages();
        // Reset file input
        document.getElementById('gallery-upload').value = '';
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage(`Error: ${errorData.error || 'Failed to upload image'}`);
      }
    } catch (error) {
      console.error('Error uploading gallery image:', error);
      setMessage('Error uploading image');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadMultipleGalleryImages = async (files) => {
    if (!files || files.length === 0) return;

    try {
      setLoading(true);
      setMessage(`Uploading ${files.length} images...`);

      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('image', file);

        try {
          const response = await fetch(`${API_BASE_URL}/api/gallery/upload`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
          console.error(`Error uploading file ${file.name}:`, error);
        }

        // Update progress
        setMessage(`Uploading: ${i + 1}/${files.length} (${successCount} success, ${failCount} failed)`);
      }

      setMessage(`Upload complete! ${successCount} uploaded successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
      fetchGalleryImages();
      // Reset file input
      document.getElementById('gallery-upload-multiple').value = '';
    } catch (error) {
      console.error('Error uploading multiple gallery images:', error);
      setMessage('Error uploading images');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGalleryImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this gallery image?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/gallery/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setMessage('Image deleted successfully!');
        fetchGalleryImages();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage(`Error: ${errorData.error || 'Failed to delete image'}`);
      }
    } catch (error) {
      console.error('Error deleting gallery image:', error);
      setMessage('Error deleting image');
    }
  };

  const handleReorderGalleryImage = async (imageId, direction) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/gallery/${imageId}/reorder`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ direction })
      });

      if (response.ok) {
        fetchGalleryImages();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage(`Error: ${errorData.error || 'Failed to reorder image'}`);
      }
    } catch (error) {
      console.error('Error reordering gallery image:', error);
      setMessage('Error reordering image');
    }
  };

  const handleAddColor = async () => {
    if (!newColorForm.colorName.trim()) {
      alert('Please enter a color name');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/colors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          color_name: newColorForm.colorName,
          color_hex: newColorForm.colorHex
        })
      });

      if (response.ok) {
        setNewColorForm({ colorName: '', colorHex: '#000000' });
        fetchColors();
        setMessage('Color added successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage(`Failed to add color: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error adding color:', error);
      setMessage('Error adding color');
    }
  };

  const handleDeleteColor = async (colorId) => {
    if (!window.confirm('Are you sure you want to delete this color? Products using this color will keep their inventory.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/colors/${colorId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        fetchColors();
        setMessage('Color deleted successfully!');
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error || 'Failed to delete color';
        console.error('Error deleting color:', errorMessage);
        setMessage(errorMessage);
      }
    } catch (error) {
      console.error('Error deleting color:', error);
      setMessage(`Error deleting color: ${error.message}`);
    }
  };

  const handleOpenEditColor = (color) => {
    setEditingColor(color);
    setEditColorForm({
      colorName: color.color_name || color.name,
      colorHex: color.color_hex || color.hex
    });
  };

  const handleSaveEditColor = async () => {
    if (!editColorForm.colorName.trim()) {
      alert('Please enter a color name');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/colors/${editingColor.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          colorName: editColorForm.colorName,
          colorHex: editColorForm.colorHex
        }),
      });

      if (response.ok) {
        fetchColors();
        setEditingColor(null);
        setMessage('Color updated successfully!');
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error || 'Failed to update color';
        console.error('Error updating color:', errorMessage);
        setMessage(errorMessage);
      }
    } catch (error) {
      console.error('Error updating color:', error);
      setMessage(`Error updating color: ${error.message}`);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      alert('Please enter a category name');
      return;
    }
    if (categories.includes(newCategory)) {
      alert('This category already exists');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: newCategory })
      });

      if (response.ok) {
        setNewCategory('');
        await fetchCategories();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error || 'Failed to add category'}`);
      }
    } catch (error) {
      console.error('Error adding category:', error);
      alert('Failed to add category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryToDelete) => {
    if (window.confirm(`Delete category "${categoryToDelete}"? Products with this category will not be affected.`)) {
      setLoading(true);
      try {
        // Find the category ID by filtering from current categories
        // Since we're working with names, we need to find the matching category
        const response = await fetch(`${API_BASE_URL}/api/categories`);
        if (response.ok) {
          const allCategories = await response.json();
          const categoryToDeleteObj = allCategories.find(cat => cat.name === categoryToDelete);

          if (categoryToDeleteObj) {
            const deleteResponse = await fetch(`${API_BASE_URL}/api/categories/${categoryToDeleteObj.id}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (deleteResponse.ok) {
              await fetchCategories();
            } else {
              alert('Failed to delete category');
            }
          }
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Failed to delete category');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditCategory = (oldName) => {
    setEditingCategory(oldName);
    setEditingCategoryName(oldName);
  };

  const handleSaveEditCategory = async () => {
    if (!editingCategoryName.trim()) {
      alert('Please enter a category name');
      return;
    }
    if (editingCategoryName !== editingCategory && categories.includes(editingCategoryName)) {
      alert('This category already exists');
      return;
    }

    setLoading(true);
    try {
      // Find the category ID
      const response = await fetch(`${API_BASE_URL}/api/categories`);
      if (response.ok) {
        const allCategories = await response.json();
        const categoryToUpdateObj = allCategories.find(cat => cat.name === editingCategory);

        if (categoryToUpdateObj) {
          const updateResponse = await fetch(`${API_BASE_URL}/api/categories/${categoryToUpdateObj.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ name: editingCategoryName })
          });

          if (updateResponse.ok) {
            setEditingCategory(null);
            setEditingCategoryName('');
            await fetchCategories();
          } else {
            const error = await updateResponse.json();
            alert(`Error: ${error.error || 'Failed to update category'}`);
          }
        }
      }
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category');
    } finally {
      setLoading(false);
    }
  };

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'products') {
      fetchProducts();
    } else if (activeTab === 'orders') {
      fetchOrders();
    } else if (activeTab === 'discounts') {
      fetchDiscounts();
    } else if (activeTab === 'wishlists') {
      fetchWishlists();
    } else if (activeTab === 'colors') {
      fetchColors();
    } else if (activeTab === 'featured') {
      fetchFeaturedProducts();
    } else if (activeTab === 'pages') {
      fetchCustomPages();
    } else if (activeTab === 'gallery') {
      fetchGalleryImages();
    }
  }, [activeTab, fetchUsers, fetchProducts, fetchOrders, fetchDiscounts, fetchWishlists, fetchColors, fetchFeaturedProducts, fetchCustomPages, fetchGalleryImages]);

  const handleAddProduct = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const productData = {
        name: formData.name,
        category: formData.category,
        price: formData.price,
        sku: formData.sku,
      };

      const response = await fetch(`${API_BASE_URL}/api/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(productData),
      });

      if (response.ok) {
        const data = await response.json();
        const newProductId = data.productId;
        setFormData({ name: '', category: '', price: '', description: '', sku: '', discountPrice: 0, stockBySize: {}, stockByColor: {} });
        navigate(`/admin/product/${newProductId}`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to add product: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error adding product:', error);
      alert('Error adding product');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadProductImage = async (productId, files) => {
    if (!files || files.length === 0) return;

    setLoading(true);
    setMessage('Compressing and uploading images...');

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Check if image needs compression
        let fileToUpload = file;
        const shouldCompress = await needsCompression(file, 800 * 1024, 1920); // 800KB, 1920px

        if (shouldCompress) {
          console.log(`Compressing ${file.name} (${(file.size / 1024).toFixed(2)}KB)`);
          try {
            fileToUpload = await compressImage(file, {
              maxWidth: 1920,
              maxHeight: 1920,
              quality: 0.85,
              mimeType: 'image/jpeg'
            });
            console.log(`Compressed to ${(fileToUpload.size / 1024).toFixed(2)}KB`);
          } catch (compressError) {
            console.warn('Compression failed, uploading original:', compressError);
            fileToUpload = file;
          }
        }

        const formData = new FormData();
        formData.append('file', fileToUpload);
        formData.append('productId', productId);
        formData.append('isPrimary', i === 0 ? 'true' : 'false');

        const response = await fetch(`${API_BASE_URL}/api/products/image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Upload failed: ${response.status} ${response.statusText}`);
        }
      }
      setMessage('Images uploaded successfully!');
      alert('Images uploaded successfully');
      fetchProducts();
    } catch (error) {
      console.error('Error uploading images:', error);
      setMessage('');
      alert('Error uploading images: ' + error.message);
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleDeleteProductImage = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        console.log('Attempting to delete image:', imageId);
        console.log('API URL:', `${API_BASE_URL}/api/products/images/${imageId}`);

        const response = await fetch(`${API_BASE_URL}/api/products/images/${imageId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });

        console.log('Delete image response status:', response.status);
        const data = await response.json();
        console.log('Delete image response data:', data);

        if (response.ok) {
          alert('Image deleted successfully');
          fetchProducts();
        } else {
          alert(`Failed to delete image: ${data.error || response.statusText}`);
        }
      } catch (error) {
        console.error('Error deleting image:', error);
        alert(`Error: ${error.message}`);
      }
    }
  };

  const handleAddDiscount = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/discounts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(discountForm),
      });

      if (response.ok) {
        alert('Discount created successfully');
        setDiscountForm({ code: '', percentage: '', expiryDate: '' });
        fetchDiscounts();
      } else {
        alert('Failed to create discount');
      }
    } catch (error) {
      console.error('Error creating discount:', error);
      alert('Error creating discount');
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async (orderId) => {
    const refundReason = window.prompt('Enter refund reason:', 'Customer request');
    if (refundReason === null) return; // User cancelled

    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}/refund`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refund_reason: refundReason }),
      });

      const data = await response.json();

      if (response.ok) {
        alert('Refund processed successfully');
        fetchOrders();
      } else {
        alert(`Failed to process refund: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error processing refund:', error);
      alert('Error processing refund');
    }
  };

  const handleOpenStatusModal = (orderId) => {
    const currentOrder = orders.find(o => o.id === orderId);
    if (!currentOrder) return;

    if (currentOrder.status === 'refunded' || currentOrder.status === 'partial_refund') {
      alert(`Cannot change status of a ${currentOrder.status} order`);
      return;
    }

    setStatusUpdateModal(currentOrder);
    setStatusUpdateForm({ status: currentOrder.status, customerMessage: '', adminNotes: '' });
  };

  const handleSubmitStatusUpdate = async () => {
    if (!statusUpdateModal || !statusUpdateForm.status) {
      alert('Please select a status');
      return;
    }

    if (statusUpdateForm.status === statusUpdateModal.status) {
      alert('Status is already set to this value');
      return;
    }

    setLoading(true);

    try {
      const url = `${API_BASE_URL}/api/orders/${statusUpdateModal.id}/status`;
      console.log('Updating order status:', { orderId: statusUpdateModal.id, newStatus: statusUpdateForm.status });

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: statusUpdateForm.status,
          notes: statusUpdateForm.adminNotes || null,
          customerMessage: statusUpdateForm.customerMessage || null
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(`Order #${statusUpdateModal.order_number} status updated successfully!`);
        setStatusUpdateModal(null);
        fetchOrders();
        setTimeout(() => setMessage(''), 3000);
      } else {
        alert(`Failed to update status: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      alert(`Error updating order status: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProductActive = async (productId, currentStatus) => {
    const action = currentStatus === 1 ? 'deactivate' : 'activate';
    if (window.confirm(`Are you sure you want to ${action} this product?`)) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/products/${productId}/toggle-active`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` },
        });

        const data = await response.json();

        if (response.ok) {
          alert(data.message || `Product ${action}d successfully`);
          fetchProducts();
        } else {
          alert(`Failed to ${action} product: ${data.error || response.statusText}`);
        }
      } catch (error) {
        console.error(`Error ${action}ing product:`, error);
        alert(`Error: ${error.message}`);
      }
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to permanently delete this product? This cannot be undone.')) {
      try {
        console.log('Attempting to delete product:', productId);
        console.log('API URL:', `${API_BASE_URL}/api/products/${productId}`);
        console.log('Token:', token);

        const response = await fetch(`${API_BASE_URL}/api/products/${productId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });

        console.log('Delete response status:', response.status);
        const data = await response.json();
        console.log('Delete response data:', data);

        if (response.ok) {
          alert('Product deleted successfully');
          fetchProducts();
        } else {
          // Show full error message from backend
          alert(data.error || `Failed to delete product: ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error deleting product:', error);
        alert(`Error: ${error.message}`);
      }
    }
  };

  const moveProductUp = (index) => {
    if (index === 0) return;
    const newProducts = [...products];
    [newProducts[index - 1], newProducts[index]] = [newProducts[index], newProducts[index - 1]];
    setProducts(newProducts);
  };

  const moveProductDown = (index) => {
    if (index === products.length - 1) return;
    const newProducts = [...products];
    [newProducts[index], newProducts[index + 1]] = [newProducts[index + 1], newProducts[index]];
    setProducts(newProducts);
  };

  const handleEnterReorderMode = async () => {
    console.log('=== ENTERING REORDER MODE ===');
    console.log('Current products count:', products.length);
    setIsReorderMode(true);
    // Fetch all products for reordering
    console.log('Calling fetchProducts(1, true) to get ALL products...');
    await fetchProducts(1, true);
  };

  const handleExitReorderMode = () => {
    setIsReorderMode(false);
    // Return to paginated view (page 1)
    fetchProducts(1, false);
  };

  const handleSaveProductOrder = async () => {
    try {
      setLoading(true);
      const productOrders = products.map((product, index) => ({
        id: product.id,
        display_order: index
      }));

      const response = await fetch(`${API_BASE_URL}/api/products/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ productOrders }),
      });

      if (response.ok) {
        alert('Product order saved successfully!');
        setIsReorderMode(false);
        // Return to paginated view (page 1)
        fetchProducts(1, false);
      } else {
        const data = await response.json();
        alert(`Failed to save product order: ${data.error || response.statusText}`);
      }
    } catch (error) {
      console.error('Error saving product order:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    if (window.confirm(`Change user role to ${newRole}?`)) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${userId}/role`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ role: newRole }),
        });

        if (response.ok) {
          alert('User role updated successfully');
          fetchUsers();
        } else {
          alert('Failed to update user role');
        }
      } catch (error) {
        console.error('Error updating user role:', error);
        alert('Error updating user role');
      }
    }
  };

  const handleDeleteCustomPage = async (pageId) => {
    if (window.confirm(`Delete page "${pageId}"?`)) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/pages/${pageId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          alert('Page deleted successfully');
          fetchCustomPages();
        } else {
          const data = await response.json();
          alert(`Failed to delete page: ${data.error || response.statusText}`);
        }
      } catch (error) {
        console.error('Error deleting page:', error);
        alert(`Error: ${error.message}`);
      }
    }
  };

  const handleToggleFeaturedProduct = async (productId, currentFeaturedStatus) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/featured-products/${productId}`, {
        method: currentFeaturedStatus ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        alert(currentFeaturedStatus ? 'Product removed from featured' : 'Product added to featured');
        fetchFeaturedProducts();
      } else {
        alert('Failed to update featured status');
      }
    } catch (error) {
      console.error('Error toggling featured status:', error);
      alert('Error updating featured status');
    }
  };

  const updateFooterConfig = async (newConfig) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/footer-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newConfig)
      });

      if (response.ok) {
        setFooterConfig(newConfig);
        // Also save to localStorage as backup
        localStorage.setItem('footerConfig', JSON.stringify(newConfig));
        console.log('Footer config saved to database and localStorage');
      } else {
        console.error('Failed to save footer config:', response.status);
      }
    } catch (error) {
      console.error('Error updating footer config:', error);
    }
  };

  const handleUpdateColumnTitle = (columnKey, newTitle) => {
    const updated = { ...footerConfigStaging };
    updated[columnKey].title = newTitle;
    setFooterConfigStaging(updated);
  };

  const handleUpdateLinkLabel = (columnKey, linkIndex, newLabel) => {
    const updated = JSON.parse(JSON.stringify(footerConfigStaging));
    if (updated[columnKey] && updated[columnKey].links && updated[columnKey].links[linkIndex]) {
      updated[columnKey].links[linkIndex].label = newLabel;
      setFooterConfigStaging(updated);
    }
  };

  const handleUpdateLinkPageId = (columnKey, linkIndex, newPageId) => {
    const updated = JSON.parse(JSON.stringify(footerConfigStaging));
    if (updated[columnKey] && updated[columnKey].links && updated[columnKey].links[linkIndex]) {
      updated[columnKey].links[linkIndex].pageId = newPageId;
      setFooterConfigStaging(updated);
    }
  };

  const handleAddLink = (columnKey) => {
    const updated = JSON.parse(JSON.stringify(footerConfigStaging));
    if (updated[columnKey] && updated[columnKey].links) {
      updated[columnKey].links.push({ label: 'New Link', pageId: '' });
      setFooterConfigStaging(updated);
    }
  };

  const handleRemoveLink = (columnKey, linkIndex) => {
    const updated = JSON.parse(JSON.stringify(footerConfigStaging));
    if (updated[columnKey] && updated[columnKey].links && updated[columnKey].links[linkIndex]) {
      updated[columnKey].links.splice(linkIndex, 1);
      setFooterConfigStaging(updated);
    }
  };

  const handleSaveFooterConfig = () => {
    updateFooterConfig(footerConfigStaging);
    setMessage('Footer configuration saved successfully!');
  };

  const handleCancelFooterChanges = () => {
    setFooterConfigStaging(footerConfig);
    setMessage('Footer changes discarded');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="admin-page">
      <aside className="admin-sidebar">
        <h2>Admin Panel</h2>
        <nav className="admin-nav">
          <button
            className={`nav-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
          <button
            className={`nav-btn ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            Products
          </button>
          <button
            className={`nav-btn ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            Orders
          </button>
          <button
            className={`nav-btn ${activeTab === 'discounts' ? 'active' : ''}`}
            onClick={() => setActiveTab('discounts')}
          >
            Discounts
          </button>
          <button
            className={`nav-btn ${activeTab === 'wishlists' ? 'active' : ''}`}
            onClick={() => setActiveTab('wishlists')}
          >
            Wishlists
          </button>
          <button
            className={`nav-btn ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categories
          </button>
          <button
            className={`nav-btn ${activeTab === 'colors' ? 'active' : ''}`}
            onClick={() => setActiveTab('colors')}
          >
            Colors
          </button>
          <button
            className={`nav-btn ${activeTab === 'featured' ? 'active' : ''}`}
            onClick={() => setActiveTab('featured')}
          >
            Featured Products
          </button>
          <button
            className={`nav-btn ${activeTab === 'pages' ? 'active' : ''}`}
            onClick={() => setActiveTab('pages')}
          >
            Custom Pages
          </button>
          <button
            className={`nav-btn ${activeTab === 'hero' ? 'active' : ''}`}
            onClick={() => setActiveTab('hero')}
          >
            Hero Images
          </button>
          <button
            className={`nav-btn ${activeTab === 'gallery' ? 'active' : ''}`}
            onClick={() => setActiveTab('gallery')}
          >
            Gallery
          </button>
          <button
            className={`nav-btn ${activeTab === 'whatsapp' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('whatsapp');
              fetchWhatsappSettings();
            }}
          >
            WhatsApp Notifications
          </button>
        </nav>
        <button className="btn-logout" onClick={handleLogout}>
          Log Out
        </button>
      </aside>

      {showPageEditor && (
        <PageEditor
          onClose={() => {
            setShowPageEditor(false);
            setSelectedPageForEdit(null);
          }}
          selectedPageId={selectedPageForEdit}
          availablePages={customPages}
        />
      )}

      {showHeroEditor && (
        <HeroEditor
          onClose={() => setShowHeroEditor(false)}
        />
      )}

      {showSimplifiedEditor && (
        <SimplifiedPageEditor
          pageId={selectedPageForEdit || newPageId}
          onClose={() => {
            setShowSimplifiedEditor(false);
            setNewPageId('');
            setSelectedPageForEdit(null);
          }}
          onSaveComplete={() => {
            setShowSimplifiedEditor(false);
            setNewPageId('');
            setSelectedPageForEdit(null);
            setNewPageForm({ pageId: '', title: '', content: '' });
            fetchCustomPages();
          }}
        />
      )}

      {showEditorChoice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            textAlign: 'center'
          }}>
            <h2 style={{ margin: '0 0 16px 0', color: '#333' }}>Choose Editor</h2>
            <p style={{ margin: '0 0 24px 0', color: '#666', fontSize: '0.95rem' }}>Which editor would you like to use?</p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowEditorChoice(false);
                  setShowSimplifiedEditor(true);
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#5568d3'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#667eea'}
              >
                Simplified Editor
              </button>
              <button
                onClick={() => {
                  setShowEditorChoice(false);
                  setShowPageEditor(true);
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#5a6268'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
              >
                HTML Editor
              </button>
            </div>
            <button
              onClick={() => {
                setShowEditorChoice(false);
                setSelectedPageForEdit(null);
              }}
              style={{
                marginTop: '16px',
                padding: '8px 16px',
                backgroundColor: 'transparent',
                color: '#666',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <main className="admin-content">
        <div className="admin-header">
          <h1>Welcome, {user?.name}!</h1>
        </div>

        {message && (
          <div className="admin-message">
            {message}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="admin-section">
            <h2>Manage Users</h2>
            <div className="admin-list">
              {users.length === 0 ? (
                <p>No users found</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Gender</th>
                      <th>Role</th>
                      <th>Country</th>
                      <th>City</th>
                      <th>Joined</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.email}</td>
                        <td>{u.name}</td>
                        <td>{u.phone || '-'}</td>
                        <td>{u.gender || '-'}</td>
                        <td>
                          <span className={`role-badge ${u.role}`}>{u.role}</span>
                        </td>
                        <td>{u.registration_country || '-'}</td>
                        <td>{u.registration_city || '-'}</td>
                        <td>{new Date(u.created_at).toLocaleDateString()}</td>
                        <td>
                          {u.role === 'customer' ? (
                            <button
                              className="btn-promote"
                              onClick={() => handleUpdateUserRole(u.id, 'admin')}
                            >
                              Promote to Admin
                            </button>
                          ) : (
                            <button
                              className="btn-demote"
                              onClick={() => handleUpdateUserRole(u.id, 'customer')}
                            >
                              Demote to Customer
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <Pagination
                currentPage={usersPagination.page}
                totalPages={usersPagination.totalPages}
                onPageChange={(page) => fetchUsers(page)}
                loading={loading}
              />
            </div>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="admin-section">
            <h2>Manage Products</h2>

            <div className="admin-form">
              <h3>Add New Product</h3>
              <form onSubmit={handleAddProduct}>
                <input
                  type="text"
                  placeholder="Product Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <input
                  type="number"
                  step="0.01"
                  placeholder="Price"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                />
                <input
                  type="text"
                  placeholder="SKU (e.g., HS-001-BLK)"
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  required
                />
                <button type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Product'}
                </button>
              </form>
            </div>

            <div className="admin-list">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3>Current Products</h3>
                <div>
                  {isReorderMode ? (
                    <>
                      <button
                        onClick={handleSaveProductOrder}
                        disabled={loading}
                        style={{
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginRight: '0.5rem'
                        }}
                      >
                        {loading ? 'Saving...' : 'Save Order'}
                      </button>
                      <button
                        onClick={handleExitReorderMode}
                        disabled={loading}
                        style={{
                          backgroundColor: '#999',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: loading ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleEnterReorderMode}
                      disabled={loading}
                      style={{
                        backgroundColor: '#2196F3',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: loading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {loading ? 'Loading...' : 'Reorder Products'}
                    </button>
                  )}
                </div>
              </div>
              {isReorderMode && (
                <div style={{
                  backgroundColor: '#E3F2FD',
                  padding: '1rem',
                  borderRadius: '4px',
                  marginBottom: '1rem',
                  fontSize: '0.95rem',
                  color: '#1976D2'
                }}>
                  <strong>Reorder Mode:</strong> Showing all {products.length} products. Use the ↑↓ arrows to rearrange, then click "Save Order" to apply changes.
                </div>
              )}
              {!isReorderMode && (
                <>
                  <input
                    type="text"
                    placeholder="Search by name, SKU, or category..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      marginBottom: '1rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.95rem',
                      fontFamily: 'inherit'
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginBottom: '1rem',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>Status:</span>
                    <button
                      onClick={() => setProductStatusFilter('all')}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: productStatusFilter === 'all' ? '#2196F3' : '#f5f5f5',
                        color: productStatusFilter === 'all' ? 'white' : '#333',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}
                    >
                      All ({products.length})
                    </button>
                    <button
                      onClick={() => setProductStatusFilter('active')}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: productStatusFilter === 'active' ? '#4caf50' : '#f5f5f5',
                        color: productStatusFilter === 'active' ? 'white' : '#333',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}
                    >
                      Active ({products.filter(p => p.is_active === 1 || p.is_active === null || p.is_active === undefined).length})
                    </button>
                    <button
                      onClick={() => setProductStatusFilter('inactive')}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: productStatusFilter === 'inactive' ? '#f44336' : '#f5f5f5',
                        color: productStatusFilter === 'inactive' ? 'white' : '#333',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '600'
                      }}
                    >
                      Hidden ({products.filter(p => p.is_active === 0).length})
                    </button>
                  </div>
                </>
              )}
              {products.length === 0 ? (
                <p>No products found</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      {isReorderMode && <th>Order</th>}
                      {isReorderMode && <th>Image</th>}
                      <th>SKU</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Price</th>
                      <th>Custom Orders</th>
                      <th>Allowed Sizes</th>
                      {!isReorderMode && <th>Images</th>}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(isReorderMode ? products : products.filter((product) => {
                      const query = productSearch.toLowerCase();
                      const matchesSearch = (
                        product.name.toLowerCase().includes(query) ||
                        product.sku.toLowerCase().includes(query) ||
                        product.category.toLowerCase().includes(query)
                      );

                      // Treat NULL/undefined as active (1)
                      const isActive = product.is_active === 1 || product.is_active === null || product.is_active === undefined;
                      const isInactive = product.is_active === 0;

                      const matchesStatus = (
                        productStatusFilter === 'all' ||
                        (productStatusFilter === 'active' && isActive) ||
                        (productStatusFilter === 'inactive' && isInactive)
                      );

                      return matchesSearch && matchesStatus;
                    })).map((product, index) => (
                      <tr key={product.id}>
                        {isReorderMode && (
                          <td>
                            <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                              <button
                                onClick={() => moveProductUp(index)}
                                disabled={index === 0}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '1rem',
                                  cursor: index === 0 ? 'not-allowed' : 'pointer',
                                  opacity: index === 0 ? 0.5 : 1
                                }}
                                title="Move up"
                              >
                                ↑
                              </button>
                              <button
                                onClick={() => moveProductDown(index)}
                                disabled={index === products.length - 1}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '1rem',
                                  cursor: index === products.length - 1 ? 'not-allowed' : 'pointer',
                                  opacity: index === products.length - 1 ? 0.5 : 1
                                }}
                                title="Move down"
                              >
                                ↓
                              </button>
                            </div>
                          </td>
                        )}
                        {isReorderMode && (
                          <td>
                            {product.images && product.images.length > 0 ? (
                              <img
                                src={product.images.find(img => img.is_primary === 1)?.image_url || product.images[0]?.image_url}
                                alt={product.name}
                                style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                              />
                            ) : (
                              <div style={{
                                width: '60px',
                                height: '60px',
                                backgroundColor: '#f0f0f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '4px',
                                fontSize: '0.7rem',
                                color: '#999'
                              }}>
                                No image
                              </div>
                            )}
                          </td>
                        )}
                        <td>{product.sku || '-'}</td>
                        <td>
                          {product.name}
                          {product.is_active === 0 && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 6px',
                              backgroundColor: '#f44336',
                              color: 'white',
                              borderRadius: '3px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}>
                              HIDDEN
                            </span>
                          )}
                        </td>
                        <td>{product.category}</td>
                        <td>${product.price}</td>
                        <td>
                          {product.allow_custom_order ? (
                            <span style={{ color: '#de4010', fontWeight: '600' }}>✓ Enabled</span>
                          ) : (
                            <span style={{ color: '#999' }}>Disabled</span>
                          )}
                        </td>
                        <td>
                          {getEnabledSizes(product).length > 0 ? (
                            <small>
                              Sizes: {getEnabledSizes(product).map(size => `EU ${size}`).join(', ')}
                            </small>
                          ) : (
                            <span style={{ color: '#999' }}>No sizes configured</span>
                          )}
                        </td>
                        {!isReorderMode && (
                          <td>
                            <div className="product-images-preview">
                            {product.images && product.images.length > 0 ? (
                              product.images.map((img, idx) => (
                                <div key={idx} className="image-item">
                                  <img
                                    src={img.image_url}
                                    alt={`${product.name}-${idx}`}
                                    style={{ maxWidth: '50px', maxHeight: '50px', margin: '2px' }}
                                  />
                                  <button
                                    className="btn-delete-image"
                                    onClick={() => handleDeleteProductImage(img.id)}
                                    title="Delete image"
                                    style={{ padding: '2px 4px', fontSize: '0.7rem', marginLeft: '2px' }}
                                  >
                                    ✕
                                  </button>
                                </div>
                              ))
                            ) : (
                              <span>No images</span>
                            )}
                          </div>
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) =>
                              setSelectedProductImages({
                                ...selectedProductImages,
                                [product.id]: e.target.files,
                              })
                            }
                            style={{ fontSize: '0.8rem', marginTop: '5px' }}
                          />
                          <button
                            className="btn-upload"
                            onClick={() =>
                              handleUploadProductImage(
                                product.id,
                                selectedProductImages[product.id]
                              )
                            }
                            disabled={!selectedProductImages[product.id]}
                            style={{ marginTop: '5px', fontSize: '0.8rem' }}
                          >
                            Upload Images
                          </button>
                        </td>
                        )}
                        <td>
                          {isReorderMode ? (
                            <span style={{ color: '#999', fontSize: '0.9rem' }}>Use arrows to reorder</span>
                          ) : (
                            <>
                              <button
                                className="btn-edit"
                                onClick={() => navigate(`/admin/product/${product.id}`)}
                              >
                                Edit
                              </button>
                              <button
                                className={product.is_active === 0 ? 'btn-success' : 'btn-delete'}
                                onClick={() => handleToggleProductActive(product.id, product.is_active)}
                                title={product.is_active === 0 ? 'Show product in store' : 'Hide product from store'}
                              >
                                {product.is_active === 0 ? 'Unhide' : 'Hide'}
                              </button>
                              <button
                                className="btn-delete"
                                onClick={() => handleDeleteProduct(product.id)}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {!isReorderMode && productStatusFilter === 'all' && (
                <Pagination
                  currentPage={productsPagination.page}
                  totalPages={productsPagination.totalPages}
                  onPageChange={(page) => fetchProducts(page)}
                  loading={loading}
                />
              )}
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="admin-section">
            <h2>Manage Orders</h2>

            <div className="admin-list">
              {orders.length === 0 ? (
                <p>No orders found</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Order Number</th>
                      <th>Customer</th>
                      <th>Total</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id}>
                        <td>{order.order_number || `#${order.id}`}</td>
                        <td>{order.user_email || 'N/A'}</td>
                        <td>${order.total}</td>
                        <td>{order.status}</td>
                        <td>{order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-status"
                              onClick={() => handleOpenStatusModal(order.id)}
                              disabled={order.status === 'refunded' || order.status === 'partial_refund'}
                            >
                              Update Status
                            </button>
                            <button
                              className="btn-refund"
                              onClick={() => handleRefund(order.id)}
                              disabled={order.status === 'refunded'}
                            >
                              {order.status === 'refunded' ? 'Refunded' : 'Refund'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <Pagination
                currentPage={ordersPagination.page}
                totalPages={ordersPagination.totalPages}
                onPageChange={(page) => fetchOrders(page)}
                loading={loading}
              />
            </div>
          </div>
        )}

        {/* Discounts Tab */}
        {activeTab === 'discounts' && (
          <div className="admin-section">
            <h2>Manage Discounts</h2>

            <div className="admin-form">
              <h3>Create Discount Code</h3>
              <form onSubmit={handleAddDiscount}>
                <input
                  type="text"
                  placeholder="Discount Code"
                  value={discountForm.code}
                  onChange={(e) => setDiscountForm({ ...discountForm, code: e.target.value })}
                  required
                />
                <input
                  type="number"
                  placeholder="Discount Percentage (0-100)"
                  min="0"
                  max="100"
                  value={discountForm.percentage}
                  onChange={(e) => setDiscountForm({ ...discountForm, percentage: e.target.value })}
                  required
                />
                <input
                  type="date"
                  value={discountForm.expiryDate}
                  onChange={(e) => setDiscountForm({ ...discountForm, expiryDate: e.target.value })}
                  required
                />
                <button type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Discount'}
                </button>
              </form>
            </div>

            <div className="admin-list">
              <h3>Active Discounts</h3>
              {discounts.length === 0 ? (
                <p>No discounts found</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Discount</th>
                      <th>Expires</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {discounts.map((discount) => (
                      <tr key={discount.id}>
                        <td>{discount.code}</td>
                        <td>{discount.percentage}%</td>
                        <td>{new Date(discount.expiry_date).toLocaleDateString()}</td>
                        <td>{new Date(discount.expiry_date) > new Date() ? 'Active' : 'Expired'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Wishlists Tab */}
        {activeTab === 'wishlists' && (
          <div className="admin-section">
            <h2>User Wishlists</h2>

            <div className="admin-list">
              {wishlists.length === 0 ? (
                <p>No wishlist items found</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>User Email</th>
                      <th>Product Name</th>
                      <th>Price</th>
                      <th>Category</th>
                      <th>Added Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wishlists.map((item) => (
                      <tr key={`${item.user_id}-${item.product_id}`}>
                        <td>{item.user_email}</td>
                        <td>{item.product_name}</td>
                        <td>${parseFloat(item.product_price).toFixed(2)}</td>
                        <td>{item.product_category}</td>
                        <td>{new Date(item.added_date).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="admin-section">
            <h2>Manage Categories</h2>

            <div className="admin-form">
              <h3>Add New Category</h3>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
                <input
                  type="text"
                  placeholder="Enter category name"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    fontSize: '0.9rem'
                  }}
                />
                <button
                  onClick={handleAddCategory}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#de4010',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  Add Category
                </button>
              </div>
            </div>

            <div className="admin-list">
              <h3>Current Categories ({categories.length})</h3>
              {categories.length === 0 ? (
                <p>No categories found</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Category Name</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((category) => (
                      <tr key={category}>
                        <td>
                          {editingCategory === category ? (
                            <input
                              type="text"
                              value={editingCategoryName}
                              onChange={(e) => setEditingCategoryName(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleSaveEditCategory()}
                              autoFocus
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #de4010'
                              }}
                            />
                          ) : (
                            category
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {editingCategory === category ? (
                              <>
                                <button
                                  onClick={handleSaveEditCategory}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem'
                                  }}
                                >
                                  Save
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingCategory(null);
                                    setEditingCategoryName('');
                                  }}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: '#999',
                                    color: 'white',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem'
                                  }}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEditCategory(category)}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: '#0066cc',
                                    color: 'white',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem'
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteCategory(category)}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    background: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem'
                                  }}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Colors Tab */}
        {activeTab === 'colors' && (
          <div className="admin-section">
            <h2>Manage Colors</h2>

            <div className="admin-form">
              <h3>Add New Color</h3>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Enter color name (e.g., Red, Blue, Black)"
                  value={newColorForm.colorName}
                  onChange={(e) => setNewColorForm({ ...newColorForm, colorName: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddColor()}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    fontSize: '0.9rem'
                  }}
                />
                <input
                  type="color"
                  value={newColorForm.colorHex}
                  onChange={(e) => setNewColorForm({ ...newColorForm, colorHex: e.target.value })}
                  style={{
                    width: '60px',
                    height: '40px',
                    border: '1px solid #ddd',
                    cursor: 'pointer'
                  }}
                  title="Pick a color"
                />
                <button
                  onClick={handleAddColor}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#de4010',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  Add Color
                </button>
              </div>
            </div>

            <div className="admin-list">
              <h3>Current Colors ({colors.length})</h3>
              {colors.length === 0 ? (
                <p>No colors found</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Color</th>
                      <th>Name</th>
                      <th>Hex Code</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {colors.map((color) => (
                      <tr key={color.id}>
                        <td>
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              backgroundColor: color.color_hex || color.hex,
                              border: '2px solid #ddd',
                              borderRadius: '4px'
                            }}
                            title={color.color_hex || color.hex}
                          />
                        </td>
                        <td>{color.color_name || color.name}</td>
                        <td style={{ fontFamily: 'monospace' }}>{color.color_hex || color.hex}</td>
                        <td style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => handleOpenEditColor(color)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: '#007bff',
                              color: 'white',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              borderRadius: '4px'
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteColor(color.id)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: '#dc3545',
                              color: 'white',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              borderRadius: '4px'
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Edit Color Dialog */}
            {editingColor && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
              }} onClick={() => setEditingColor(null)}>
                <div style={{
                  background: 'white',
                  borderRadius: '8px',
                  padding: '2rem',
                  maxWidth: '500px',
                  width: '90%',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                }} onClick={(e) => e.stopPropagation()}>
                  <h2 style={{ marginTop: 0, marginBottom: '1.5rem' }}>Edit Color</h2>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Color Name:
                    </label>
                    <input
                      type="text"
                      value={editColorForm.colorName}
                      onChange={(e) => setEditColorForm({ ...editColorForm, colorName: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '0.95rem',
                        boxSizing: 'border-box'
                      }}
                      placeholder="Enter color name"
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                      Color Hex:
                    </label>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                      <input
                        type="color"
                        value={editColorForm.colorHex}
                        onChange={(e) => setEditColorForm({ ...editColorForm, colorHex: e.target.value })}
                        style={{
                          width: '60px',
                          height: '40px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      />
                      <input
                        type="text"
                        value={editColorForm.colorHex}
                        onChange={(e) => setEditColorForm({ ...editColorForm, colorHex: e.target.value })}
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.95rem',
                          fontFamily: 'monospace'
                        }}
                        placeholder="#000000"
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setEditingColor(null)}
                      style={{
                        padding: '0.75rem 1.5rem',
                        border: '1px solid #ddd',
                        background: '#fff',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEditColor}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: '#de4010',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                      }}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Featured Products Tab */}
        {activeTab === 'featured' && (
          <div className="admin-section">
            <h2>Manage Featured Products</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              Select products to feature in the carousel on the home page.
            </p>

            <div className="admin-list">
              <h3>All Products ({products.length})</h3>
              <input
                type="text"
                placeholder="Search by name, SKU, or category..."
                value={featuredProductSearch}
                onChange={(e) => setFeaturedProductSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  marginBottom: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '0.95rem',
                  fontFamily: 'inherit'
                }}
              />
              {loading ? (
                <p>Loading products...</p>
              ) : products.length === 0 ? (
                <p>No products found</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Product Name</th>
                      <th>Category</th>
                      <th>Price</th>
                      <th>Featured</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.filter((product) => {
                      const query = featuredProductSearch.toLowerCase();
                      return (
                        product.name.toLowerCase().includes(query) ||
                        product.sku.toLowerCase().includes(query) ||
                        product.category.toLowerCase().includes(query)
                      );
                    }).map((product) => {
                      const isFeatured = featuredProducts.some(fp => fp.id === product.id);
                      return (
                        <tr key={product.id}>
                          <td>{product.name}</td>
                          <td>{product.category || '-'}</td>
                          <td>${parseFloat(product.price).toFixed(2)}</td>
                          <td>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              background: isFeatured ? '#d4edda' : '#f8f9fa',
                              color: isFeatured ? '#155724' : '#666'
                            }}>
                              {isFeatured ? '✓ Featured' : 'Not Featured'}
                            </span>
                          </td>
                          <td>
                            <button
                              onClick={() => handleToggleFeaturedProduct(product.id, isFeatured)}
                              style={{
                                padding: '0.5rem 1rem',
                                background: isFeatured ? '#dc3545' : '#de4010',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                borderRadius: '4px'
                              }}
                            >
                              {isFeatured ? 'Remove' : 'Add to Featured'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {featuredProducts.length > 0 && (
              <div className="admin-list" style={{ marginTop: '2rem' }}>
                <h3>Featured Products Order ({featuredProducts.length})</h3>
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  Products will appear in the carousel in the order shown below (left to right).
                </p>
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Product Name</th>
                      <th>Category</th>
                      <th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {featuredProducts.map((product, index) => (
                      <tr key={product.id}>
                        <td style={{ fontWeight: '600', color: '#de4010' }}>{index + 1}</td>
                        <td>{product.name}</td>
                        <td>{product.category || '-'}</td>
                        <td>${parseFloat(product.price).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Custom Pages Tab */}
        {activeTab === 'pages' && (
          <div className="admin-section">
            <h2>Manage Custom Pages</h2>

            {/* Choose Page Creation Method */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#f0f7ff', borderRadius: '4px', border: '2px solid #667eea' }}>
              <h3 style={{ marginTop: 0, color: '#667eea' }}>Create New Page</h3>
              <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                Choose how you want to create your new page:
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                {/* Simplified Editor Option */}
                <div style={{
                  padding: '1.5rem',
                  backgroundColor: 'white',
                  border: '1px solid #667eea',
                  borderRadius: '4px'
                }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>Simplified Editor</h4>
                  <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>
                    No HTML required. Just add text and upload a background image.
                  </p>
                  <input
                    type="text"
                    placeholder="e.g., terms-of-service"
                    value={newPageForm.pageId}
                    onChange={(e) => setNewPageForm({ ...newPageForm, pageId: e.target.value.toLowerCase() })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      marginBottom: '0.5rem',
                      border: '1px solid #ddd',
                      fontSize: '0.85rem',
                      boxSizing: 'border-box',
                      borderRadius: '3px'
                    }}
                  />
                  <small style={{ color: '#888', display: 'block', marginBottom: '1rem' }}>
                    Page ID (URL slug)
                  </small>
                  <button
                    type="button"
                    onClick={() => {
                      if (newPageForm.pageId) {
                        setNewPageId(newPageForm.pageId);
                        setShowSimplifiedEditor(true);
                      } else {
                        alert('Please enter a Page ID');
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      borderRadius: '3px'
                    }}
                  >
                    Use Simplified Editor
                  </button>
                </div>

                {/* HTML Editor Option */}
                <div style={{
                  padding: '1.5rem',
                  backgroundColor: 'white',
                  border: '1px solid #de4010',
                  borderRadius: '4px'
                }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>HTML Editor</h4>
                  <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>
                    Full control with HTML support. For advanced users.
                  </p>
                  <input
                    type="text"
                    placeholder="e.g., terms-of-service"
                    value={newPageForm.pageId}
                    onChange={(e) => setNewPageForm({ ...newPageForm, pageId: e.target.value.toLowerCase() })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      marginBottom: '0.5rem',
                      border: '1px solid #ddd',
                      fontSize: '0.85rem',
                      boxSizing: 'border-box',
                      borderRadius: '3px'
                    }}
                  />
                  <small style={{ color: '#888', display: 'block', marginBottom: '1rem' }}>
                    Page ID (URL slug)
                  </small>
                  <button
                    type="button"
                    onClick={() => {
                      if (newPageForm.pageId) {
                        setSelectedPageForEdit(newPageForm.pageId);
                        setShowPageEditor(true);
                        setNewPageForm({ pageId: '', title: '', content: '' });
                      } else {
                        alert('Please enter a Page ID');
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: '#de4010',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      borderRadius: '3px'
                    }}
                  >
                    Use HTML Editor
                  </button>
                </div>
              </div>
            </div>

            {/* Footer Links Management */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#f9f9f9', borderRadius: '4px', border: '1px solid #eee' }}>
              <h3 style={{ marginTop: 0 }}>Footer Links Management</h3>
              <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                Configure the footer navigation columns. The newsletter signup and social icons are managed separately.
              </p>

              {/* Column 1 */}
              <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #ddd' }}>
                <h4 style={{ marginBottom: '1rem' }}>Column 1: {footerConfigStaging.column1?.title}</h4>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                    Column Title:
                  </label>
                  <input
                    type="text"
                    value={footerConfigStaging.column1?.title || ''}
                    onChange={(e) => handleUpdateColumnTitle('column1', e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '300px',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.9rem' }}>
                    Links:
                  </label>
                  {footerConfigStaging.column1?.links?.map((link, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', alignItems: 'flex-start' }}>
                      <input
                        type="text"
                        placeholder="Link Label"
                        value={link.label}
                        onChange={(e) => handleUpdateLinkLabel('column1', idx, e.target.value)}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          boxSizing: 'border-box'
                        }}
                      />
                      <select
                        value={link.pageId}
                        onChange={(e) => handleUpdateLinkPageId('column1', idx, e.target.value)}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          boxSizing: 'border-box'
                        }}
                      >
                        <option value="">Select page...</option>
                        {customPages.map((page) => (
                          <option key={page.id} value={page.id}>
                            {page.title} ({page.id})
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleRemoveLink('column1', idx)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => handleAddLink('column1')}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.85rem'
                    }}
                  >
                    + Add Link
                  </button>
                </div>
              </div>

              {/* Column 2 */}
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ marginBottom: '1rem' }}>Column 2: {footerConfigStaging.column2?.title}</h4>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                    Column Title:
                  </label>
                  <input
                    type="text"
                    value={footerConfigStaging.column2?.title || ''}
                    onChange={(e) => handleUpdateColumnTitle('column2', e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: '300px',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', fontSize: '0.9rem' }}>
                    Links:
                  </label>
                  {footerConfigStaging.column2?.links?.map((link, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', alignItems: 'flex-start' }}>
                      <input
                        type="text"
                        placeholder="Link Label"
                        value={link.label}
                        onChange={(e) => handleUpdateLinkLabel('column2', idx, e.target.value)}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          boxSizing: 'border-box'
                        }}
                      />
                      <select
                        value={link.pageId}
                        onChange={(e) => handleUpdateLinkPageId('column2', idx, e.target.value)}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          boxSizing: 'border-box'
                        }}
                      >
                        <option value="">Select page...</option>
                        {customPages.map((page) => (
                          <option key={page.id} value={page.id}>
                            {page.title} ({page.id})
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleRemoveLink('column2', idx)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => handleAddLink('column2')}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.85rem'
                    }}
                  >
                    + Add Link
                  </button>
                </div>
              </div>
            </div>

            <div className="admin-list">
              <h3>Existing Custom Pages ({customPages.length})</h3>
              {customPages.length === 0 ? (
                <p>No custom pages created yet</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Page ID</th>
                      <th>Title</th>
                      <th>URL</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customPages.map((page) => (
                      <tr key={page.id}>
                        <td><code>{page.id}</code></td>
                        <td>{page.title}</td>
                        <td>
                          <a href={`/page/${page.id}`} target="_blank" rel="noopener noreferrer" style={{ color: '#de4010', textDecoration: 'none' }}>
                            /page/{page.id}
                          </a>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              onClick={() => {
                                setSelectedPageForEdit(page.id);
                                setShowEditorChoice(true);
                              }}
                              style={{
                                padding: '0.5rem 1rem',
                                background: '#007bff',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '0.85rem'
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteCustomPage(page.id)}
                              style={{
                                padding: '0.5rem 1rem',
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '0.85rem'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

              {/* Footer Save/Cancel Buttons */}
              <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleCancelFooterChanges}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  Cancel Changes
                </button>
                <button
                  onClick={handleSaveFooterConfig}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#de4010',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600'
                  }}
                >
                  Save Footer Configuration
                </button>
              </div>
          </div>
        )}

        {/* Hero Images Tab */}
        {activeTab === 'hero' && (
          <div className="admin-section">
            <h2>Hero Images Management</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              Manage the hero banner images that appear on your website homepage. Top banner is a single large image, and bottom banner is a slideshow.
            </p>

            <button
              onClick={() => setShowHeroEditor(true)}
              style={{
                padding: '1rem 2rem',
                background: '#de4010',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              Open Hero Images Editor
            </button>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="admin-section">
            <h2>Gallery Management</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              Manage gallery images shown in the mobile menu. Each image can link to a product or an external URL.
            </p>

            <div className="admin-form">
              <h3>Add Gallery Images</h3>
              <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Upload single or multiple images to the gallery.
              </p>

              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '250px' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                    Single Image Upload
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    id="gallery-upload"
                    style={{ width: '100%' }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleUploadGalleryImage(file);
                      }
                    }}
                  />
                </div>

                <div style={{ flex: '1', minWidth: '250px' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                    Multiple Images Upload
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    id="gallery-upload-multiple"
                    style={{ width: '100%' }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 0) {
                        handleUploadMultipleGalleryImages(files);
                      }
                    }}
                  />
                </div>
              </div>

              {message && activeTab === 'gallery' && (
                <div style={{
                  padding: '0.75rem',
                  marginBottom: '1rem',
                  backgroundColor: message.includes('Error') ? '#fee' : '#efe',
                  border: `1px solid ${message.includes('Error') ? '#fcc' : '#cfc'}`,
                  borderRadius: '4px',
                  color: message.includes('Error') ? '#c00' : '#060'
                }}>
                  {message}
                </div>
              )}
            </div>

            <div className="admin-list">
              <h3>Gallery Images ({galleryImages.length})</h3>
              {galleryImages.length === 0 ? (
                <p>No gallery images yet</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Linked To</th>
                      <th>Order</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {galleryImages.map((image, index) => (
                      <tr key={image.id}>
                        <td>
                          <img
                            src={image.image_url}
                            alt="Gallery"
                            style={{
                              width: '120px',
                              height: '120px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              border: '2px solid #e0e0e0',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          />
                        </td>
                        <td>
                          {image.product_id ? (
                            <span>Product ID: {image.product_id}</span>
                          ) : image.external_url ? (
                            <a href={image.external_url} target="_blank" rel="noopener noreferrer">
                              {image.external_url}
                            </a>
                          ) : (
                            <span style={{ color: '#999' }}>No link</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span>{image.display_order}</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                              <button
                                onClick={() => handleReorderGalleryImage(image.id, 'up')}
                                disabled={index === 0}
                                style={{
                                  padding: '0.1rem 0.4rem',
                                  fontSize: '0.7rem',
                                  cursor: index === 0 ? 'not-allowed' : 'pointer',
                                  opacity: index === 0 ? 0.3 : 1,
                                  background: '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '2px'
                                }}
                                title="Move up"
                              >
                                ▲
                              </button>
                              <button
                                onClick={() => handleReorderGalleryImage(image.id, 'down')}
                                disabled={index === galleryImages.length - 1}
                                style={{
                                  padding: '0.1rem 0.4rem',
                                  fontSize: '0.7rem',
                                  cursor: index === galleryImages.length - 1 ? 'not-allowed' : 'pointer',
                                  opacity: index === galleryImages.length - 1 ? 0.3 : 1,
                                  background: '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '2px'
                                }}
                                title="Move down"
                              >
                                ▼
                              </button>
                            </div>
                          </div>
                        </td>
                        <td>
                          <button
                            className="btn-delete"
                            onClick={() => handleDeleteGalleryImage(image.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* WhatsApp Business API Settings Tab */}
        {activeTab === 'whatsapp' && (
          <div className="admin-section">
            <h2>WhatsApp Business API Notifications</h2>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              Configure WhatsApp Business API credentials to receive WhatsApp notifications for orders and contact form submissions. Notifications will be sent to your admin account's phone number and to customers when applicable.
            </p>

            <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#f9f9f9', borderRadius: '4px', border: '1px solid #eee' }}>
              <h3 style={{ marginTop: 0 }}>WhatsApp Business API Configuration</h3>

              <form onSubmit={handleUpdateWhatsappSettings}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                    Phone Number ID:
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your WhatsApp Phone Number ID"
                    value={whatsappSettings.whatsappPhoneId}
                    onChange={(e) => setWhatsappSettings({ ...whatsappSettings, whatsappPhoneId: e.target.value })}
                    style={{
                      width: '100%',
                      maxWidth: '600px',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      boxSizing: 'border-box',
                      fontFamily: 'monospace'
                    }}
                  />
                  <p style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.5rem' }}>
                    Get your Phone Number ID from Meta for Developers
                  </p>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>
                    Access Token:
                  </label>
                  <input
                    type="password"
                    placeholder="Enter your WhatsApp Access Token"
                    value={whatsappSettings.whatsappAccessToken}
                    onChange={(e) => setWhatsappSettings({ ...whatsappSettings, whatsappAccessToken: e.target.value })}
                    style={{
                      width: '100%',
                      maxWidth: '600px',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      boxSizing: 'border-box',
                      fontFamily: 'monospace'
                    }}
                  />
                  <p style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.5rem' }}>
                    Get your Access Token from Meta for Developers
                  </p>
                </div>

                <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107', color: '#856404' }}>
                  <strong>ℹ️ How to get your WhatsApp Business API credentials:</strong>
                  <ol style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem', fontSize: '0.85rem' }}>
                    <li>Sign up for Meta for Developers at <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" style={{ color: '#856404' }}>developers.facebook.com</a></li>
                    <li>Create a new app and select "Business" as the app type</li>
                    <li>Add WhatsApp product to your app</li>
                    <li>Navigate to WhatsApp → API Setup in the sidebar</li>
                    <li>Copy your Phone Number ID from the "From" field</li>
                    <li>Generate and copy a permanent Access Token (or use a temporary one for testing)</li>
                    <li>Paste both credentials above</li>
                  </ol>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ marginBottom: '1rem' }}>Notification Settings</h4>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={whatsappSettings.notificationsEnabled}
                      onChange={(e) => setWhatsappSettings({ ...whatsappSettings, notificationsEnabled: e.target.checked })}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>Enable all notifications</span>
                  </label>

                  {whatsappSettings.notificationsEnabled && (
                    <div style={{ marginLeft: '1.5rem', paddingLeft: '1rem', borderLeft: '2px solid #de4010' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={whatsappSettings.notifyOnOrders}
                          onChange={(e) => setWhatsappSettings({ ...whatsappSettings, notifyOnOrders: e.target.checked })}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.85rem' }}>Notify on new orders</span>
                      </label>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={whatsappSettings.notifyOnContact}
                          onChange={(e) => setWhatsappSettings({ ...whatsappSettings, notifyOnContact: e.target.checked })}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.85rem' }}>Notify on contact form submissions</span>
                      </label>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                  <button
                    type="submit"
                    disabled={whatsappLoading}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: whatsappLoading ? '#ccc' : '#de4010',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: whatsappLoading ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}
                  >
                    {whatsappLoading ? 'Saving...' : 'Save Settings'}
                  </button>

                  <button
                    type="button"
                    onClick={handleTestWhatsapp}
                    disabled={whatsappLoading}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: whatsappLoading ? '#ccc' : '#0066cc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: whatsappLoading ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}
                  >
                    {whatsappLoading ? 'Testing...' : 'Test Connection'}
                  </button>
                </div>

                {whatsappMessage && (
                  <div style={{
                    padding: '0.75rem 1rem',
                    borderRadius: '4px',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    background: whatsappMessage.includes('✓') ? '#d4edda' : '#f8d7da',
                    color: whatsappMessage.includes('✓') ? '#155724' : '#721c24',
                    border: `1px solid ${whatsappMessage.includes('✓') ? '#c3e6cb' : '#f5c6cb'}`
                  }}>
                    {whatsappMessage}
                  </div>
                )}
              </form>
            </div>

            <div style={{ padding: '1.5rem', backgroundColor: '#e8f4f8', borderRadius: '4px', border: '1px solid #b3e5fc' }}>
              <h3 style={{ marginTop: 0, color: '#01579b' }}>ℹ️ About WhatsApp Business API Notifications</h3>
              <ul style={{ fontSize: '0.85rem', lineHeight: '1.6', color: '#01579b', marginBottom: 0 }}>
                <li><strong>Admin notifications:</strong> You'll receive WhatsApp notifications on your admin account's phone number</li>
                <li><strong>Order notifications:</strong> You'll receive detailed customer info (name, email, phone, location), order details, and all contact methods (WhatsApp, email, social media) when orders are placed</li>
                <li><strong>Custom order notifications:</strong> You'll be notified when customers request custom orders with full details</li>
                <li><strong>Contact form notifications:</strong> Customer contact attempts will be sent to your WhatsApp with multiple ways to reach them</li>
                <li><strong>Customer notifications:</strong> Customers will also receive WhatsApp confirmations if their phone is linked to WhatsApp</li>
                <li><strong>Privacy:</strong> Your credentials are stored securely and only used to send notifications</li>
                <li><strong>Testing:</strong> Click "Test Connection" to verify your WhatsApp Business API credentials and send a test message to your admin phone</li>
              </ul>
            </div>
          </div>
        )}

        {/* Status Update Modal */}
        {statusUpdateModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '100%',
              padding: '2rem',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '1rem', color: '#333' }}>
                Update Order Status
              </h2>

              <p style={{ fontSize: '0.95rem', color: '#666', marginBottom: '1.5rem' }}>
                <strong>Order #{statusUpdateModal.order_number}</strong><br />
                Current Status: <span style={{ fontWeight: '600', color: '#0066cc' }}>{statusUpdateModal.status}</span>
              </p>

              {/* Status Dropdown */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                  New Status *
                </label>
                <select
                  value={statusUpdateForm.status}
                  onChange={(e) => setStatusUpdateForm({ ...statusUpdateForm, status: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: 'inherit'
                  }}
                >
                  <option value="">Select a status...</option>
                  <option value="pending">🔄 Pending</option>
                  <option value="processing">📦 Processing</option>
                  <option value="shipped">🚚 Shipped</option>
                  <option value="delivered">✅ Delivered</option>
                  <option value="cancelled">❌ Cancelled</option>
                </select>
              </div>

              {/* Customer Message */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                  Message to Customer (sent via WhatsApp)
                </label>
                <textarea
                  value={statusUpdateForm.customerMessage}
                  onChange={(e) => setStatusUpdateForm({ ...statusUpdateForm, customerMessage: e.target.value })}
                  placeholder="Add a personalized message for the customer..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '0.95rem',
                    fontFamily: 'inherit',
                    minHeight: '100px',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
                <p style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem', marginBottom: 0 }}>
                  This message will be sent to the customer's WhatsApp along with the status update
                </p>
              </div>

              {/* Admin Notes */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>
                  Internal Notes (not sent to customer)
                </label>
                <textarea
                  value={statusUpdateForm.adminNotes}
                  onChange={(e) => setStatusUpdateForm({ ...statusUpdateForm, adminNotes: e.target.value })}
                  placeholder="Add notes for your team..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '0.95rem',
                    fontFamily: 'inherit',
                    minHeight: '80px',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setStatusUpdateModal(null)}
                  disabled={loading}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#f0f0f0',
                    color: '#333',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitStatusUpdate}
                  disabled={loading || !statusUpdateForm.status}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: statusUpdateForm.status && statusUpdateForm.status !== statusUpdateModal.status ? '#0066cc' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (loading || !statusUpdateForm.status) ? 'not-allowed' : 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: '600'
                  }}
                >
                  {loading ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
